import React from 'react';
import './GuidesPage.css';
import { Helmet } from 'react-helmet';

// --- IMPORTED COMPONENTS ---
import { Accordion } from '../components/Accordion'; // Assuming path is correct
import { Tooltip } from '../components/ToolTip';     // Assuming path is correct
import ShinyText from '../components/ShinyText';     // Assuming path is correct


// Helper component for each guide section
const GuideSection = ({ title, children }) => (
  <section className="guide-section">
    <h2 className="guide-section-title">{title}</h2>
    {children}
  </section>
);

// Helper component for "Pro-Tip" callouts
const ProTip = ({ children }) => (
  <div className="pro-tip">
    <span className="pro-tip-icon">💡</span>
    <div className="pro-tip-content">
      <strong>Pro Tip:</strong> {children}
    </div>
  </div>
);

// Helper component for important facts
const FactBox = ({ children }) => (
  <div className="fact-box">
    <span className="fact-box-icon">ℹ️</span>
    <div className="fact-box-content">
      {children}
    </div>
  </div>
);

// --- NEW COMPONENT ---
// Helper for creating styled external links with a tooltip
const ExternalLink = ({ href, children }) => (
  <Tooltip tip="Learn more">
    <a href={href} target="_blank" rel="noopener noreferrer" className="external-link">
      {children}
    </a>
  </Tooltip>
);


const GuidesPage = () => {
  return (
    <div className="guides-page-container">
      {/* Helmet for SEO remains the same */}
      <Helmet>
        <title>PC Building Guide – Learn How to Build Your Dream Computer | PC Build Guru</title>
        <meta
          name="description"
          content="The ultimate PC building guide from PC Build Guru. Learn about CPUs, GPUs, RAM, storage, motherboards, PSUs, and more with expert tips and brand insights."
        />
        <meta
          name="keywords"
          content="PC building guide, how to build a PC, custom PC, PC components, CPU, GPU, RAM, motherboard, storage, PSU"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.pcbuildguru.com/guides" />

        {/* Open Graph */}
        <meta property="og:title" content="PC Building Guide – Learn How to Build Your Dream Computer" />
        <meta
          property="og:description"
          content="Master the art of building a PC with PC Build Guru's complete guide to components, performance, and budget optimization."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.pcbuildguru.com/guides" />
        <meta property="og:site_name" content="PC Build Guru" />
        <meta property="og:image" content="https://www.pcbuildguru.com/images/pc-building-guide-og.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PC Building Guide – Learn How to Build Your Dream Computer" />
        <meta
          name="twitter:description"
          content="Learn everything about building your own PC, from choosing the right CPU to selecting a quality PSU."
        />
        <meta name="twitter:image" content="https://www.pcbuildguru.com/images/pc-building-guide-twitter.jpg" />

        {/* Structured Data: HowTo */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "PC Building Guide",
            "description": "Step-by-step guide to building a custom PC, including choosing components, assembling them, and optimizing performance.",
            "totalTime": "PT4H",
            "estimatedCost": {
              "@type": "MonetaryAmount",
              "currency": "INR",
              "value": "50000"
            },
            "step": [
              {
                "@type": "HowToStep",
                "name": "Choose Your CPU",
                "text": "Select a CPU based on your use case, budget, and desired performance."
              },
              {
                "@type": "HowToStep",
                "name": "Select a GPU",
                "text": "Pick a GPU that matches your gaming or rendering needs."
              },
              {
                "@type": "HowToStep",
                "name": "Pick the Right Motherboard",
                "text": "Ensure compatibility with CPU, RAM, and expansion options."
              },
              {
                "@type": "HowToStep",
                "name": "Install RAM and Storage",
                "text": "Choose and install memory and storage based on capacity and speed."
              },
              {
                "@type": "HowToStep",
                "name": "Install PSU and Case",
                "text": "Select a reliable PSU and a case with good airflow."
              }
            ]
          })}
        </script>
      </Helmet>

      <header className="guides-header">
        <ShinyText text="The Ultimate PC Building Guide" />
        <p>From beginner basics to advanced insights, this guide will help you understand every component in your future custom PC.</p>
      </header>

      <div className="guides-content">
        <GuideSection title="The Core Components: Your PC's Engine">
          <h3>1. The CPU (Central Processing Unit)</h3>
          <p><strong>What it is:</strong> The "brain" of your computer. It performs the calculations and executes the instructions that make everything run. A faster CPU means a snappier, more responsive computer.</p>
          <ul>
            <li>
                <Tooltip tip="More cores help the CPU handle multiple tasks (like gaming + streaming) at once.">
                    <strong>Cores & Threads:</strong>
                </Tooltip> For gaming, 4-6 cores is often enough. For video editing or streaming, 8 or more cores is ideal.
            </li>
            <li>
                <Tooltip tip="Measures speed. A higher number is generally faster for single-threaded tasks like many games.">
                    <strong>Clock Speed (GHz):</strong>
                </Tooltip> A higher clock speed generally means faster performance.
            </li>
          </ul>
          
          <Accordion title="The Story of the Titans: Intel vs. AMD">
            <p>For decades, the world of CPUs has been a battlefield dominated by two giants. On one side, you have <strong>Intel</strong>, known for its powerful "Core" series (i3, i5, i7, i9). Intel has historically focused on achieving the highest possible performance on a single core, making them fantastic for gaming.</p>
            <p>On the other side is <strong>AMD</strong>, who rose to prominence with its "Ryzen" architecture by offering more cores for the money. This made them champions of multitasking. In recent years, the competition has become incredibly close, making the "best" choice dependent on your budget at the time of purchase.</p>
          </Accordion>

          <ProTip>Don't overspend on the CPU for a purely gaming build. Most modern games rely more heavily on the GPU. A mid-range CPU is the sweet spot for most gamers.</ProTip>
          
          <h3>2. The GPU (Graphics Processing Unit)</h3>
          <p><strong>What it is:</strong> The most important component for gaming, 3D rendering, and AI. It's a specialized processor designed to handle complex visual data.</p>
          <ul>
            <li>
                <Tooltip tip="The GPU's dedicated high-speed memory. More VRAM is needed for higher resolutions and detailed textures.">
                    <strong>VRAM (Video RAM):</strong>
                </Tooltip> For 1080p gaming, 8GB is a good target. For 1440p or 4K, aim for 12GB or more.
            </li>
            <li><strong>The "Tier" Matters Most:</strong> A "GeForce RTX 4070" will always be more powerful than an "RTX 4060," regardless of the brand that makes the card.</li>
          </ul>

          <Accordion title="The Graphics Duel: NVIDIA vs. AMD Radeon">
              <p>Much like the CPU space, the GPU market is a fierce two-horse race. <strong>NVIDIA</strong>, with its "GeForce" line, is the market leader. They pioneered <Tooltip tip="A rendering technique for generating an image by tracing the path of light as pixels."><strong>ray tracing</strong></Tooltip> for realistic lighting and developed <ExternalLink href="https://en.wikipedia.org/wiki/Deep_Learning_Super_Sampling">DLSS</ExternalLink>, an AI feature that boosts frame rates.</p>
              <p><strong>AMD</strong>, with its "Radeon" line, competes by offering incredible raw performance at a competitive price. Their answer to DLSS is <ExternalLink href="https://www.amd.com/en/products/graphics/technologies/fidelityfx.html">FSR</ExternalLink> (FidelityFX Super Resolution), which works on a wider range of cards.</p>
          </Accordion>

          <h3>3. RAM (Random Access Memory)</h3>
          <p><strong>What it is:</strong> Your PC's short-term memory. It holds data for currently running applications. Not enough RAM can cause your system to slow down.</p>
          <ul>
             <li>
                <Tooltip tip="16GB is great for gaming. 32GB is better for content creation. 64GB+ is for professional workstations.">
                    <strong>Capacity:</strong>
                </Tooltip> 16GB is the new standard, 32GB is recommended for heavy multitasking.
            </li>
            <li>
                <Tooltip tip="DDR5 is the newest and fastest standard, but DDR4 is a great budget-friendly option. Your motherboard choice dictates which you can use.">
                    <strong>Speed (MHz) & Type (DDR4/DDR5):</strong>
                </Tooltip> Faster RAM can provide a small performance boost.
            </li>
          </ul>
          <ProTip>Always buy RAM in a kit of two sticks (e.g., 2x8GB for 16GB total). This allows it to run in <Tooltip tip="Using two RAM sticks allows the CPU to access memory faster, significantly improving performance."><strong>dual-channel mode</strong></Tooltip>, which is much faster than a single stick.</ProTip>
        </GuideSection>

        <GuideSection title="The Foundation: Motherboard, Storage & Power">
            <h3>4. The Motherboard: The Unsung Hero</h3>
            <p><strong>What it is:</strong> The central nervous system. It's the main circuit board that every single component plugs into, defining the capabilities and personality of your PC.</p>
            <Accordion title="More on Motherboards: Features & Philosophies">
                <p>A motherboard dictates how your PC can grow. A cheap motherboard might limit future upgrades. A high-end one might have more slots for RAM and SSDs, built-in Wi-Fi, and a more robust power delivery system for <Tooltip tip="Running a component at a higher clock speed than its official rating to squeeze out more performance."><strong>overclocking</strong></Tooltip>.</p>
                <p>Intel often requires a new motherboard "chipset" with each new CPU generation. AMD is famous for supporting multiple generations of CPUs on the same socket (like AM4), making them a favorite for builders who like to upgrade often.</p>
            </Accordion>
            <ul>
                <li>
                    <Tooltip tip="The motherboard's 'brain.' Higher-end chipsets (Z-series for Intel, X-series for AMD) unlock features like CPU overclocking.">
                        <strong>Chipset (e.g., B760, Z790):</strong>
                    </Tooltip> A mid-range "B" series chipset is the perfect balance for most users.
                </li>
                <li>
                    <Tooltip tip="The physical size of the motherboard. ATX is standard, Micro-ATX is smaller, and Mini-ITX is for tiny PCs.">
                        <strong>Form Factor (ATX, Micro-ATX):</strong>
                    </Tooltip> This must match the size supported by your PC case.
                </li>
            </ul>

          <h3>5. Storage (SSD & HDD)</h3>
          <p><strong>What it is:</strong> Your PC's long-term memory, where your OS, programs, and files are stored.</p>
          <ul>
            <li>
                <Tooltip tip="Solid State Drive: Uses flash memory for extremely fast load times. Your OS should always be on an SSD.">
                    <strong>SSD:</strong>
                </Tooltip> Essential for a modern PC. <Tooltip tip="The fastest type of consumer SSD, plugs directly into the motherboard."><strong>NVMe M.2</strong></Tooltip> SSDs are the best.
            </li>
            <li>
                <Tooltip tip="Hard Disk Drive: Older, slower technology using spinning platters. Great for cheap mass storage of large files.">
                    <strong>HDD:</strong>
                </Tooltip> Slower, but much cheaper for storing large files like movies or photos.
            </li>
          </ul>
          <FactBox>A PC with a fast SSD will feel much snappier in day-to-day use than a PC with a faster CPU but only a traditional hard drive.</FactBox>

          <h3>6. The PSU (Power Supply Unit)</h3>
          <p><strong>What it is:</strong> Supplies power to all components. A poor-quality PSU can damage your entire system, so this is not a part to cheap out on.</p>
          <ul>
            <li>
                <Tooltip tip="The total power output. Sum the needs of your CPU and GPU, then add ~30% extra for safety and future upgrades.">
                    <strong>Wattage:</strong>
                </Tooltip> You need enough to power your system with some headroom.
            </li>
            <li>
                <Tooltip tip="Higher ratings (Bronze, Gold, Platinum) are more power-efficient, run cooler, and are generally more reliable.">
                    <strong>Efficiency Rating (80+):</strong>
                </Tooltip> Look for at least an "80+ Bronze" rating.
            </li>
          </ul>
          <ProTip>A <Tooltip tip="Allows you to detach unused cables for a cleaner build and better airflow."><strong>modular</strong></Tooltip> PSU is a quality-of-life feature that's worth the small extra cost.</ProTip>
        </GuideSection>

        <GuideSection title="The Case (Chassis): Your PC's Home & Armor">
            <h3>7. The PC Case: More Than Just a Box</h3>
            <p><strong>What it is:</strong> The case protects all your expensive components from dust and damage, provides the structural framework for your build, and is critical for directing airflow to keep everything cool. It's also the primary way you express the aesthetic of your build.</p>

            <h4>Key Considerations Before You Buy:</h4>
            <ul>
                <li>
                    <Tooltip tip="The size standard for motherboards. Your case must be designed to fit your chosen motherboard's form factor.">
                        <strong>Motherboard Compatibility:</strong>
                    </Tooltip> The most important factor. A case designed for a large <strong>ATX</strong> motherboard can usually fit smaller <strong>Micro-ATX</strong> or <strong>Mini-ITX</strong> boards, but not the other way around. Always match the case to your motherboard's size.
                </li>
                <li>
                    <strong>Component Clearance:</strong> Just because a motherboard fits doesn't mean your other parts will.
                    <ul>
                        <li>
                            <Tooltip tip="Measured in millimeters (mm). High-end graphics cards can be very long, so check the case's spec sheet!">
                                <strong>GPU Length:</strong>
                            </Tooltip> A common "gotcha" for first-time builders. Ensure the case is deep enough for your graphics card.
                        </li>
                        <li>
                            <Tooltip tip="Large air coolers for CPUs can be very tall. The case must be wide enough to close the side panel.">
                                <strong>CPU Cooler Height:</strong>
                            </Tooltip> If you're not using a liquid cooler, this is a crucial measurement.
                        </li>
                    </ul>
                </li>
                <li>
                    <Tooltip tip="The path air takes through your case. Good airflow uses intake fans (front/bottom) to pull cool air in and exhaust fans (rear/top) to push hot air out.">
                        <strong>Airflow:</strong>
                    </Tooltip> A case's ability to move air is vital for performance and longevity. Cases with a mesh front panel almost always offer better airflow than those with a solid front panel.
                </li>
            </ul>

            <Accordion title="Understanding Case Types: Full-Tower, Mid-Tower, and SFF">
                <p><strong>Full-Tower:</strong> These are massive cases built for enthusiasts with custom water-cooling loops, multiple graphics cards, and extensive storage arrays. They offer maximum space but are large, heavy, and expensive.</p>
                <p><strong>Mid-Tower:</strong> The most popular choice and the sweet spot for almost everyone. They are large enough for a standard ATX motherboard and most high-end components, offering a great balance of size, features, and compatibility.</p>
                <p><strong>Mini-Tower & <Tooltip tip="Small Form Factor: A category of compact cases for space-saving builds.">SFF</Tooltip>:</strong> These are for compact builds. While they save a lot of desk space, they can be challenging to build in due to tight clearances and may require specialized, smaller components (like an SFX power supply).</p>
            </Accordion>

            <ProTip>
                Check professional reviews for thermal performance charts. A well-designed case from a reputable brand like <ExternalLink href="https://nzxt.com/category/cases-and-cooling">NZXT</ExternalLink>, <ExternalLink href="https://www.corsair.com/us/en/c/pc-cases">Corsair</ExternalLink>, <ExternalLink href="https://www.fractal-design.com/products/cases/">Fractal Design</ExternalLink>, or <ExternalLink href="https://www.lien-li.com/cases/">Lian Li</ExternalLink> can significantly lower your component temperatures.
            </ProTip>
        </GuideSection>

        <GuideSection title="Visual Learner? Watch the Experts">
            <p>Reading is great, but sometimes the best way to learn is to watch. We've compiled a list of essential video topics that cover everything from picking parts to the final build. Just paste your favorite creator's link for each topic!</p>

            <Accordion title="Part 1: Choosing Your Components">
                <ul className="video-link-list">
                    <li><ExternalLink href="https://youtu.be/c9783HUFXOI">How to Choose a CPU</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/8hyWXJe_wts">How to Choose a Graphics Card (GPU)</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/fsIh2eoWLT4">How to Pick a Motherboard</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/OGr1UEMt2Io">How Much RAM Do You Actually Need?</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/1u1w8vp1bCs">SSD vs. HDD: Choosing Your Storage</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/SS7P63fLE_8">Choosing the Right Power Supply (PSU)</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/vhyqtZx0NHM">A Guide to Picking the Perfect PC Case</ExternalLink></li>
                </ul>
            </Accordion>

            <Accordion title="Part 2: Step-by-Step Assembly Guides">
                <ul className="video-link-list">
                    <li><ExternalLink href="https://youtu.be/DC-Xn2C_L1U">The Ultimate First-Person PC Build Guide (POV)</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/c3dggnkaEs8">How to Cable Manage Your PC Like a Pro</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/-8LMML_EiMg">Top 10 PC Building Mistakes to Avoid</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/uTmPwlFmv3c">How to Install an AIO Liquid Cooler</ExternalLink></li>
                </ul>
            </Accordion>

            <Accordion title="Part 3: Post-Build Setup & Optimization">
                <ul className="video-link-list">
                    <li><ExternalLink href="https://youtu.be/LVV_mihEh6g">What to Do After Building Your PC (Installing Windows & Drivers)</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/5tbSB6aBY9E">How to Enable XMP/EXPO in BIOS for Full RAM Speed</ExternalLink></li>
                    <li><ExternalLink href="https://youtu.be/-bab7HjoZqk">How to Benchmark and Stress-Test Your New PC</ExternalLink></li>
                </ul>
            </Accordion>

            <FactBox>
                None of the videos above are sponsored or affiliated. They are simply the best resources we've found to help you build your PC.
            </FactBox>

            <FactBox>
                Technology moves fast! When watching component recommendation videos, always check the publication date to ensure the information is current and relevant for today's market.
            </FactBox>
        </GuideSection>
      </div>
    </div>
  );
};

export default GuidesPage;
// --- END OF GUIDES PAGE COMPONENT ---